import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Check, CreditCard } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Semaphore } from "@/components/Semaphore";
import { createClient, updateClient } from "@/lib/api";

const EMPTY = {
  client_code: "",
  full_name: "",
  email: "",
  phone: "",
  payment_methods: [],
  terminal: "",
  hotel: "",
  status: "gris",
  notes: "",
  currency: "USD",
  total_amount: 0,
  assigned_to: "",
};

export const ClientFormDialog = ({ open, onOpenChange, client, methods, labels, terminals = [], onSaved }) => {
  const isEdit = !!client;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (client) {
        setForm({
          client_code: client.client_code || "",
          full_name: client.full_name || "",
          email: client.email || "",
          phone: client.phone || "",
          payment_methods: client.payment_methods || (client.payment_method ? [client.payment_method] : []),
          terminal: client.terminal || "",
          hotel: client.hotel || "",
          status: client.status || "gris",
          notes: client.notes || "",
          currency: client.currency || "USD",
          total_amount: client.total_amount || 0,
          assigned_to: client.assigned_to || "",
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, client]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleMethod = (m) =>
    set(
      "payment_methods",
      form.payment_methods.includes(m)
        ? form.payment_methods.filter((x) => x !== m)
        : [...form.payment_methods, m]
    );

  const submit = async () => {
    if (!form.full_name.trim()) {
      toast.error("El nombre completo es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const saved = isEdit
        ? await updateClient(client.id, form)
        : await createClient(form);
      toast.success(isEdit ? "Cliente actualizado" : `Cliente creado (${saved.client_code})`);
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(msg || (isEdit ? "No se pudo actualizar" : "No se pudo crear el cliente"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] p-0 gap-0 flex flex-col" data-testid="client-form">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Editar Cliente" : "Nuevo Cliente"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Todos los datos son editables." : "Registra un nuevo cliente en el sistema."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 px-6 py-4 overflow-y-auto flex-1">
          <div className="grid gap-1.5">
            <Label htmlFor="cc">ID Cliente</Label>
            <Input
              id="cc"
              data-testid="client-code-input"
              value={form.client_code}
              onChange={(e) => set("client_code", e.target.value)}
              placeholder="Ej. CLI-1001 (se genera automáticamente si lo dejas vacío)"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="fn">Nombre completo *</Label>
            <Input
              id="fn"
              data-testid="client-name-input"
              value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)}
              placeholder="Ej. Ana María Gómez"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="em">Correo electrónico</Label>
              <Input
                id="em"
                data-testid="client-email-input"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="correo@mail.com"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ph">Teléfono / WhatsApp</Label>
              <Input
                id="ph"
                data-testid="client-phone-input"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+52 ..."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="ht">Hotel / Destino</Label>
              <Input
                id="ht"
                data-testid="client-hotel-input"
                value={form.hotel}
                onChange={(e) => set("hotel", e.target.value)}
                placeholder="Ej. Hotel Xcaret Arte"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="as">Asesor / Agente asignado</Label>
              <Input
                id="as"
                data-testid="client-assigned-input"
                value={form.assigned_to}
                onChange={(e) => set("assigned_to", e.target.value)}
                placeholder="ej. valeria_m o admin"
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Formas de pago (multipago)</Label>
            <p className="text-xs text-slate-400 -mt-1">
              Selecciona una o varias (ej. una parte con tarjeta y otra con transferencia).
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              {methods.map((m) => {
                const active = form.payment_methods.includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    data-testid={`payment-toggle-${m}`}
                    onClick={() => toggleMethod(m)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-all ${
                      active
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {active && <Check className="h-3.5 w-3.5" />}
                    {m}
                  </button>
                );
              })}
              {methods.length === 0 && (
                <p className="text-sm text-slate-400">
                  No hay formas de pago. Añádelas desde "Formas de Pago".
                </p>
              )}
            </div>

            {/* Selector de Terminal si se elige Terminal como forma de pago */}
            {(form.payment_methods.some((m) => m.toLowerCase().includes("terminal")) || form.terminal) && (
              <div className="mt-2.5 rounded-lg border border-blue-200/80 bg-blue-50/50 p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-blue-900 flex items-center gap-1.5">
                    <CreditCard className="h-4 w-4 text-blue-600" /> Terminal de Cobro
                  </Label>
                  <span className="text-[11px] text-blue-600">¿Con qué terminal se cobró?</span>
                </div>
                <select
                  data-testid="client-terminal-select"
                  value={form.terminal}
                  onChange={(e) => set("terminal", e.target.value)}
                  className="w-full h-9 rounded-md border border-blue-200 bg-white px-3 text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Sin terminal asignada --</option>
                  {form.terminal && !terminals.some((t) => t.name === form.terminal) && (
                    <option value={form.terminal}>
                      {form.terminal} (Histórica / Anterior)
                    </option>
                  )}
                  {terminals.map((t) => (
                    <option key={t.id} value={t.name}>
                      {t.name} {t.status === "bloqueada" ? " [BLOQUEADA]" : " (Activa)"}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label>Estatus (semáforo)</Label>
            <div className="pt-1">
              <Semaphore status={form.status} labels={labels} onChange={(v) => set("status", v)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="ta">Monto total de la reserva</Label>
              <Input
                id="ta"
                type="number"
                min="0"
                step="0.01"
                data-testid="client-total-input"
                value={form.total_amount}
                onChange={(e) => set("total_amount", parseFloat(e.target.value) || 0)}
                placeholder="0.00"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Moneda</Label>
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="client-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD — Dólar</SelectItem>
                  <SelectItem value="MXN">MXN — Peso mexicano</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="nt">Notas operativas</Label>
            <Textarea
              id="nt"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Notas internas..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-100">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button data-testid="submit-client-form" onClick={submit} disabled={saving}>
            {saving ? "Guardando..." : isEdit ? "Guardar Cambios" : "Crear Cliente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
